import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Ellipsis } from 'lucide-react';
import type { TicketStatus } from '@brimo/shared';
import { Button, Icon, MenuItem, MenuList, Popover, Skeleton, useToast } from '../../../ds/components';
import { clsx } from '../../../ds/clsx';
import { ApiError, errorMessage } from '../../../lib/api';
import { usePermissions } from '../../../lib/session';
import { LockedPage } from '../../../shell/ModulePages';
import {
  assignTicket,
  changeTicketStatus,
  patchTicket,
  remindTicket,
  takeTicket,
  useInvalidateTickets,
  useTicket,
} from '../api';
import { CreateCaseDialog } from '../after-sales/CreateCaseDialog';
import type { TicketDetail } from '../types';
import { LoadError, isForbidden } from '../ui/LoadError';
import { TicketPill } from '../ui/TicketPill';
import page from '../ui/page.module.css';
import { AssignDialog } from './AssignDialog';
import { ReplyBox } from './ReplyBox';
import { Sidebar } from './Sidebar';
import { StatusSegments } from './StatusSegments';
import { Thread } from './Thread';
import styles from './detail.module.css';

type DialogKind = 'assign' | 'after-sales';

/**
 * `/tickets/:id` — the ticket record (Backoffice 05 new/unassigned, 06 exceeded with an internal note, 07 waiting,
 * 08 reopened): header with the status switch, the assignment banner, the thread, the reply box or the waiting strip,
 * and the sidebar. Every action answers with the fresh `TicketDetail`; the realtime event refreshes other tabs.
 */
export function TicketPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const { toast } = useToast();
  const invalidate = useInvalidateTickets();
  const ticket = useTicket(id);
  const [dialog, setDialog] = useState<DialogKind | null>(null);
  const [pending, setPending] = useState(false);

  if (permissions.tool('tickets') !== 'ok' || isForbidden(ticket.error)) return <LockedPage tool="tickets" />;

  async function run(action: () => Promise<TicketDetail>) {
    setPending(true);
    try {
      await action();
      await invalidate();
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setPending(false);
    }
  }

  const detail = ticket.data;
  if (!detail) {
    const notFound = ticket.error instanceof ApiError && ticket.error.status === 404;
    return (
      <div className={page.page}>
        <Crumbs number={null} />
        {ticket.isError ? (
          <LoadError
            what={notFound ? 'Dieses Ticket gibt es nicht mehr' : 'Das Ticket konnte nicht geladen werden'}
            error={ticket.error}
            retrying={ticket.isFetching}
            onRetry={() => void ticket.refetch()}
          />
        ) : (
          <div className={page.skeleton} aria-busy="true">
            <Skeleton width={520} height={34} />
            <div className={styles.grid}>
              <Skeleton variant="block" height={320} />
              <Skeleton variant="block" height={420} />
            </div>
          </div>
        )}
      </div>
    );
  }

  const resolveAllowed = detail.segments.some((segment) => segment.key === 'resolved' && segment.allowed);
  const setStatus = (to: TicketStatus) => void run(() => changeTicketStatus(detail.id, to));
  const menu: { key: string; label: string; onSelect: () => void }[] = [];
  if (detail.sidebar.afterSales) {
    const linked = detail.sidebar.afterSales;
    menu.push({
      key: 'open-case',
      label: `After-Sales-Vorgang ${linked.numberLabel} öffnen`,
      onSelect: () => void navigate(`/tickets/after-sales/${linked.caseId}`),
    });
  } else if (detail.actions.createAfterSales) {
    menu.push({
      key: 'create-case',
      label: 'After-Sales-Vorgang anlegen …',
      onSelect: () => setDialog('after-sales'),
    });
  }

  return (
    <div className={page.page}>
      <div className={styles.head}>
        <div className={styles.headText}>
          <Crumbs number={detail.numberLabel} />
          <div className={styles.titleRow}>
            <h1 className={styles.title}>{detail.title}</h1>
            <TicketPill label={detail.pill.label} tone={detail.pill.tone} />
          </div>
          <div className={styles.subline}>{detail.subline}</div>
        </div>
        <div className={styles.headActions}>
          <StatusSegments segments={detail.segments} pending={pending} onChange={setStatus} />
          <OverflowMenu entries={menu} />
        </div>
      </div>

      <div className={styles.grid}>
        <div className={styles.main}>
          {detail.banner ? (
            <div className={styles.assignBanner}>
              <span className={styles.assignText}>{detail.banner.text}</span>
              {detail.actions.assign ? (
                <Button
                  variant="secondary"
                  size="small"
                  disabled={pending}
                  onClick={() => setDialog('assign')}
                >
                  Zuweisen
                </Button>
              ) : null}
              {detail.actions.take ? (
                <Button loading={pending} onClick={() => void run(() => takeTicket(detail.id))}>
                  Übernehmen
                </Button>
              ) : null}
            </div>
          ) : null}

          <Thread items={detail.thread} />

          {detail.reply ? (
            <ReplyBox
              key={detail.id}
              ticketId={detail.id}
              reply={detail.reply}
              onSent={() => void invalidate()}
            />
          ) : null}

          {detail.waiting ? (
            <div className={styles.waitingStrip}>
              <span className={styles.waitingText}>{detail.waiting.text}</span>
              {detail.actions.remind ? (
                <Button
                  variant="secondary"
                  size="small"
                  disabled={pending}
                  onClick={() =>
                    void run(async () => {
                      const updated = await remindTicket(detail.id);
                      toast({ message: 'Die Erinnerung ist unterwegs.' });
                      return updated;
                    })
                  }
                >
                  Erinnerung senden
                </Button>
              ) : null}
              {resolveAllowed ? (
                <Button
                  variant="secondary"
                  size="small"
                  disabled={pending}
                  onClick={() => setStatus('resolved')}
                >
                  Als gelöst markieren
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>

        <Sidebar
          detail={detail}
          pending={pending}
          onAssign={(assigneeId) => void run(() => assignTicket(detail.id, assigneeId))}
          onCategory={(categoryId) => void run(() => patchTicket(detail.id, { categoryId }))}
        />
      </div>

      {dialog === 'assign' ? (
        <AssignDialog
          ticketLabel={detail.numberLabel}
          assignees={detail.options.assignees}
          defaultId={detail.banner?.defaultAssigneeId ?? null}
          onAssign={async (assigneeId) => {
            await assignTicket(detail.id, assigneeId);
            await invalidate();
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === 'after-sales' ? (
        <CreateCaseDialog
          draft={{
            ticketId: detail.id,
            title: detail.title,
            description: firstCustomerText(detail),
            propertyId: detail.sidebar.reference?.propertyId ?? null,
            unitId: detail.sidebar.reference?.unitId ?? null,
          }}
          onClose={(createdId) => {
            setDialog(null);
            if (createdId) void navigate(`/tickets/after-sales/${createdId}`);
          }}
        />
      ) : null}
    </div>
  );
}

/** The customer's first message becomes the case description. */
function firstCustomerText(detail: TicketDetail): string {
  const first = detail.thread.find((item) => item.kind === 'message' && !item.sender.staff && !item.internal);
  return first?.kind === 'message' ? first.text : '';
}

function Crumbs({ number }: { number: string | null }) {
  return (
    <nav aria-label="Brotkrumen" className={styles.crumbs}>
      <Link to="/tickets" className={styles.crumbLink}>
        Tickets
      </Link>
      {number ? (
        <>
          <span aria-hidden="true">/</span>
          <span className={styles.crumbNumber} aria-current="page">
            {number}
          </span>
        </>
      ) : null}
    </nav>
  );
}

function OverflowMenu({ entries }: { entries: { key: string; label: string; onSelect: () => void }[] }) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  if (entries.length === 0) return null;
  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={clsx('pressable', styles.moreButton)}
        aria-label="Weitere Aktionen"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <Icon icon={Ellipsis} size={16} />
      </button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        width={260}
        placement="bottom-end"
        variant="card"
        initialFocus="none"
      >
        <MenuList label="Weitere Aktionen" density="compact" onClose={() => setOpen(false)}>
          {entries.map((entry) => (
            <MenuItem
              key={entry.key}
              label={entry.label}
              onSelect={() => {
                setOpen(false);
                entry.onSelect();
              }}
            />
          ))}
        </MenuList>
      </Popover>
    </>
  );
}
